/**
 * Integrytas Group - Base App (MOBILE)
 * Page: DSE.REPORTING — mobile-native.
 *
 * Touch-first counterpart of src/pages/admin/DseReportingPage.tsx. A DISTINCT
 * presentation (KPI tiles + view chips + scannable card lists + bottom-sheet
 * detail) over the SAME read-only data layer — never the desktop grid re-hosted
 * (§16). Two views over the DSE shadow-field reporting surface:
 *   - "Columns": the flattened report columns a Dynamic Data View can point at
 *     for a chosen entity, via the dse_view_columns(p_entity) RPC (entities come
 *     from the dse_entity table, is_enabled='Y'). Each column already carries a
 *     resolved label + type from the RPC, so no raw id/code reaches render (§17.B4).
 *   - "Index advisor": hot custom columns filtered/sorted often but not yet
 *     indexed, via the dse_index_recommendations(p_min_usage) RPC.
 *
 * This screen is READ-ONLY. The desktop's one-click index creation is a DDL RPC
 * (dse_apply_index), NOT a Common-Routine business write, so it has no mobile
 * counterpart here — mutations must funnel through routedUpdate/routedInsert
 * (§2), and there is no such business write on this surface. Consequently this
 * file imports neither routed_writes nor any mutation path.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, X, Database, KeyRound, TrendingUp, Clock, Layers } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { useFormatDateTime } from '../../lib/format_date';
import { MobilePageHeader, MobileStatStrip, MobileContentCard, MobileCardRow, MobileDetailRow, MobileFilterChips, MobileStatusPill } from '../components';
import type { MobilePageComponentProps } from '../mobile_page_components';

// ---------------------------------------------------------------------------
// Row shapes (mirror the desktop RPC payloads exactly — do not invent columns).
// ---------------------------------------------------------------------------

interface ViewCol {
  key: string;
  label: string;
  data_type: string;
  sql_type: string;
  is_indexed: boolean;
}

interface ViewMeta {
  entity: string;
  view: string;
  columns: ViewCol[];
}

interface Rec {
  entity: string;
  key: string;
  label: string;
  data_type: string;
  filter_count: number;
  sort_count: number;
  total: number;
  last_used_at: string | null;
}

type ViewFacet = 'columns' | 'advisor';

/** Discriminated selection for the detail bottom-sheet. */
type Selected =
  | { kind: 'column'; row: ViewCol; entity: string }
  | { kind: 'rec'; row: Rec };

/** Usage thresholds offered as chips in the advisor view (mirrors the desktop's
 *  numeric "min usage" input, presented touch-first). */
const MIN_USAGE_OPTIONS = [1, 5, 10, 20] as const;
const DEFAULT_MIN_USAGE = 5;

export function MobileDseReportingPage(_props: MobilePageComponentProps) {
  const { t } = useTranslation();
  const formatDateTime = useFormatDateTime();

  const [view, setView] = useState<ViewFacet>('columns');
  const [entities, setEntities] = useState<string[]>([]);
  const [entity, setEntity] = useState('');
  const [meta, setMeta] = useState<ViewMeta | null>(null);
  const [recs, setRecs] = useState<Rec[]>([]);
  const [minUsage, setMinUsage] = useState<number>(DEFAULT_MIN_USAGE);
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState<Selected | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Enabled DSE entities — the pickable set for the Columns view.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data, error: e } = await apiDb
        .from('dse_entity')
        .select('entity_name')
        .eq('is_enabled', 'Y')
        .order('entity_name');
      if (cancelled) return;
      if (e) { setError(e.message); return; }
      setEntities(((data ?? []) as { entity_name: string }[]).map((r) => r.entity_name));
    })();
    return () => { cancelled = true; };
  }, []);

  const loadColumns = async (ent: string) => {
    if (!ent) { setMeta(null); return; }
    setBusy(true);
    setError(null);
    try {
      const { data, error: e } = await apiDb.rpc<ViewMeta>('dse_view_columns', { p_entity: ent });
      if (e) throw e;
      setMeta(data ?? null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => { void loadColumns(entity); }, [entity]);

  const loadRecs = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data, error: e } = await apiDb.rpc<Rec[]>('dse_index_recommendations', { p_min_usage: minUsage });
      if (e) throw e;
      setRecs(Array.isArray(data) ? data : []);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  // loadRecs is re-created each render; reload only when the threshold changes
  // (runs on mount too). Depending on `loadRecs`/`t` would loop under test.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void loadRecs(); }, [minUsage]);

  const filteredColumns = useMemo(() => {
    const cols = meta?.columns ?? [];
    const q = filter.trim().toLowerCase();
    if (!q) return cols;
    return cols.filter(
      (c) =>
        c.label.toLowerCase().includes(q) ||
        c.key.toLowerCase().includes(q) ||
        c.data_type.toLowerCase().includes(q),
    );
  }, [meta, filter]);

  const filteredRecs = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return recs;
    return recs.filter(
      (r) =>
        r.label.toLowerCase().includes(q) ||
        r.entity.toLowerCase().includes(q) ||
        r.key.toLowerCase().includes(q),
    );
  }, [recs, filter]);

  const isColumns = view === 'columns';

  // View-scoped KPI totals.
  const columnTotals = useMemo(() => {
    const cols = meta?.columns ?? [];
    const indexed = cols.filter((c) => c.is_indexed).length;
    return { total: cols.length, indexed, unindexed: cols.length - indexed };
  }, [meta]);

  const advisorTotals = useMemo(() => {
    return recs.reduce(
      (acc, r) => ({
        filters: acc.filters + (r.filter_count ?? 0),
        sorts: acc.sorts + (r.sort_count ?? 0),
      }),
      { filters: 0, sorts: 0 },
    );
  }, [recs]);

  const viewFacets: { key: ViewFacet; label: string }[] = [
    { key: 'columns', label: t('mobile.dse_reporting.view.columns', 'Report columns') },
    { key: 'advisor', label: t('mobile.dse_reporting.view.advisor', 'Index advisor') },
  ];

  const emptyList = isColumns
    ? !entity || filteredColumns.length === 0
    : filteredRecs.length === 0;

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.dse_reporting.title', 'Shadow-Field Reporting')}
        description={t('mobile.dse_reporting.description', 'Report columns and index recommendations for shadow-field data.')}
      />

      <MobileStatStrip
        stats={isColumns ? [
          { value: entities.length.toLocaleString(), label: t('mobile.dse_reporting.kpi.entities', 'Entities') },
          { value: meta ? columnTotals.total.toLocaleString() : '—', label: t('mobile.dse_reporting.kpi.columns', 'Columns') },
          { value: meta ? columnTotals.indexed.toLocaleString() : '—', label: t('mobile.dse_reporting.kpi.indexed', 'Indexed') },
          { value: meta ? columnTotals.unindexed.toLocaleString() : '—', label: t('mobile.dse_reporting.kpi.unindexed', 'Unindexed') },
        ] : [
          { value: recs.length.toLocaleString(), label: t('mobile.dse_reporting.kpi.recommendations', 'Advised') },
          { value: advisorTotals.filters.toLocaleString(), label: t('mobile.dse_reporting.kpi.filters', 'Filters') },
          { value: advisorTotals.sorts.toLocaleString(), label: t('mobile.dse_reporting.kpi.sorts', 'Sorts') },
          { value: minUsage.toLocaleString(), label: t('mobile.dse_reporting.kpi.min_usage', 'Min usage') },
        ]}
      />

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={
            isColumns
              ? t('mobile.dse_reporting.search_columns', 'Search columns')
              : t('mobile.dse_reporting.search_recs', 'Search recommendations')
          }
          aria-label={
            isColumns
              ? t('mobile.dse_reporting.search_columns', 'Search columns')
              : t('mobile.dse_reporting.search_recs', 'Search recommendations')
          }
        />
      </div>

      <MobileFilterChips
        chips={viewFacets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => view === v}
        onSelect={(v) => { setView(v as ViewFacet); setFilter(''); }}
      />

      {/* Columns view: entity picker chips. Advisor view: min-usage threshold chips. */}
      {isColumns ? (
        entities.length > 0 && (
          <MobileFilterChips
            chips={entities.map((en) => ({ value: en, label: en }))}
            isActive={(v) => entity === v}
            onSelect={(v) => setEntity((cur) => (cur === v ? '' : v))}
          />
        )
      ) : (
        <MobileFilterChips
          chips={MIN_USAGE_OPTIONS.map((n) => ({
            value: String(n),
            label: t('mobile.dse_reporting.min_n', { count: n, defaultValue: `min ${n}` }),
          }))}
          isActive={(v) => minUsage === Number(v)}
          onSelect={(v) => setMinUsage(Number(v))}
        />
      )}

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {emptyList && !busy ? (
        <MobileContentCard>
          <div className="m-empty">
            <Inbox />{' '}
            {isColumns
              ? entity
                ? t('mobile.dse_reporting.no_columns', 'No custom columns for this entity')
                : t('mobile.dse_reporting.pick_entity', 'Pick an entity to see its report columns')
              : t('mobile.dse_reporting.no_recs', 'No recommendations at this usage threshold')}
          </div>
        </MobileContentCard>
      ) : isColumns ? (
        <MobileContentCard list>
          {filteredColumns.map((c) => (
            <MobileCardRow
              key={c.key}
              lead={c.is_indexed ? <KeyRound /> : <Database />}
              leadTone={c.is_indexed ? 'green' : 'slate'}
              title={c.label}
              subtitle={`${c.key} · ${c.data_type}`}
              trailing={
                <MobileStatusPill tone={c.is_indexed ? 'ok' : 'no'}>
                  {c.is_indexed
                    ? t('mobile.dse_reporting.indexed', 'Indexed')
                    : t('mobile.dse_reporting.not_indexed', 'Not indexed')}
                </MobileStatusPill>
              }
              chevron
              onClick={() => setSelected({ kind: 'column', row: c, entity })}
            />
          ))}
        </MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filteredRecs.map((r) => (
            <MobileCardRow
              key={`${r.entity}.${r.key}`}
              lead={<TrendingUp />}
              leadTone="amber"
              title={r.label}
              subtitle={`${r.entity} · ${t('mobile.dse_reporting.fs_counts', {
                filters: r.filter_count,
                sorts: r.sort_count,
                defaultValue: `${r.filter_count} filters · ${r.sort_count} sorts`,
              })}`}
              trailing={
                <MobileStatusPill tone="info">
                  {t('mobile.dse_reporting.total_n', { count: r.total, defaultValue: `${r.total} uses` })}
                </MobileStatusPill>
              }
              chevron
              onClick={() => setSelected({ kind: 'rec', row: r })}
            />
          ))}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet (read-only breakdown) */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => setSelected(null)} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.dse_reporting.detail', 'Column detail')}>
        <div className="m-grabber" />
        {selected?.kind === 'column' && (
          <>
            <div className="m-sheet-id">
              <span className="m-av">{selected.row.is_indexed ? <KeyRound size={20} /> : <Database size={20} />}</span>
              <span>
                <div className="m-nm">{selected.row.label}</div>
                <div className="m-em">{selected.entity}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow label={t('mobile.dse_reporting.field.column', 'Virtual column')} value={selected.row.key} />
              <MobileDetailRow label={t('mobile.dse_reporting.field.type', 'Type')} value={selected.row.data_type} />
              <MobileDetailRow label={t('mobile.dse_reporting.field.sql_cast', 'SQL cast')} value={selected.row.sql_type} />
              <MobileDetailRow
                label={t('mobile.dse_reporting.field.indexed', 'Indexed')}
                value={
                  <MobileStatusPill tone={selected.row.is_indexed ? 'ok' : 'no'}>
                    {selected.row.is_indexed
                      ? t('mobile.dse_reporting.indexed', 'Indexed')
                      : t('mobile.dse_reporting.not_indexed', 'Not indexed')}
                  </MobileStatusPill>
                }
              />
            </MobileContentCard>
            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-ghost" onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
        {selected?.kind === 'rec' && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><TrendingUp size={20} /></span>
              <span>
                <div className="m-nm">{selected.row.label}</div>
                <div className="m-em">{selected.row.entity}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow label={t('mobile.dse_reporting.field.column', 'Virtual column')} value={selected.row.key} />
              <MobileDetailRow label={t('mobile.dse_reporting.field.type', 'Type')} value={selected.row.data_type} />
              <MobileDetailRow
                label={t('mobile.dse_reporting.field.filters', 'Filters')}
                value={<><Layers size={14} /> {selected.row.filter_count.toLocaleString()}</>}
              />
              <MobileDetailRow label={t('mobile.dse_reporting.field.sorts', 'Sorts')} value={selected.row.sort_count.toLocaleString()} />
              <MobileDetailRow
                label={t('mobile.dse_reporting.field.total', 'Total uses')}
                value={<MobileStatusPill tone="info">{selected.row.total.toLocaleString()}</MobileStatusPill>}
              />
              <MobileDetailRow
                label={t('mobile.dse_reporting.field.last_used', 'Last used')}
                value={<><Clock size={14} /> {selected.row.last_used_at ? formatDateTime(selected.row.last_used_at) : '—'}</>}
              />
            </MobileContentCard>
            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-ghost" onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
